/* ME → WRITING — her own writing rules (voice-layers spec, layer 6).
 *
 * What she writes here goes into every Momentum drafting prompt and the Social
 * Generator, and beats the general voice rules and her Voice Profile. It never
 * beats the truth floor (no invented results, no fake urgency), and the screen
 * does not pretend otherwise.
 *
 * The Momentum screen pattern, on a Me screen: NOTHING TO PRESS. A choice
 * saves on the press, a typed field on the way out of it, and the read-out
 * goes amber and says so when a save fails — "Saved" and silence look the same.
 * Canvas tokens throughout, the same surface as Settings.
 *
 * LEARNED RULES arrive here too, later (build piece 2): her decision
 * 2026-09-23 was that they add themselves, marked as learned, and she edits or
 * removes them like her own. So a learned rule is an ordinary row with a tag.
 */
import { useEffect, useRef, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { ChevronDown, ChevronRight, PenLine, Plus, Sparkles, Trash2 } from "lucide-react";
import { DEFAULT_ASK, LEARNING_TOOLS, VOICE_TOOLS, WRITING_ASK, WRITING_HABITS, WRITING_URGENCY, newRule, readWriting, setTool, toggleHabit, toolSummary, writingForSave } from "../utils/writing";

const card = { background: "var(--bg-surface)", borderRadius: 12, padding: 20, boxShadow: "var(--shadow-soft)" };
const heading = { margin: "0 0 4px", fontSize: 13, fontWeight: 700, color: "var(--text-primary)", textTransform: "uppercase", letterSpacing: "0.5px" };
const sub = { margin: "0 0 12px", fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.5 };
const section = { margin: "8px 0 -8px", fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.08em" };
const field = {
  width: "100%", padding: "9px 12px", borderRadius: 8, border: "1.5px solid var(--border-strong)",
  fontSize: 14, outline: "none", fontFamily: "inherit", boxSizing: "border-box",
  background: "var(--input-bg)", color: "var(--text-primary)",
};

function Choice({ on, onClick, children, title }) {
  return (
    <button onClick={onClick} aria-pressed={on} title={title}
      style={{
        padding: "7px 14px", borderRadius: 18, fontSize: 13, fontWeight: on ? 700 : 500, cursor: "pointer", fontFamily: "inherit",
        border: `1.5px solid ${on ? "var(--accent)" : "var(--border-strong)"}`,
        background: on ? "var(--accent-quiet)" : "transparent",
        color: on ? "var(--accent)" : "var(--text-secondary)",
      }}>
      {children}
    </button>
  );
}

export function WritingPanel({ writing: stored, onSave, isMobile }) {
  const [w, setW] = useState(() => readWriting(stored));
  const [status, setStatus] = useState("idle"); // idle | saving | saved | failed
  const focusId = useRef(null);
  /* Loaded after the screen mounted (a slow settings read): take it, unless
     she has already started changing things. */
  const touched = useRef(false);
  useEffect(() => {
    if (touched.current) return;
    setW(readWriting(stored));
    lastSaved.current = JSON.stringify(writingForSave(readWriting(stored)));
  }, [stored]);

  /* What storage holds now, so leaving a field she did not change writes
     nothing. A failed save leaves it as it was, so the next blur retries. */
  const lastSaved = useRef(JSON.stringify(writingForSave(readWriting(stored))));
  const save = async (next) => {
    touched.current = true;
    setW(next);
    const payload = writingForSave(next);
    const key = JSON.stringify(payload);
    if (key === lastSaved.current) return;
    setStatus("saving");
    const ok = await onSave(payload);
    if (ok) lastSaved.current = key;
    setStatus(ok ? "saved" : "failed");
  };
  /* Typed fields: the screen follows every keystroke, storage only the way out.
     THE WAY OUT SAVES `latest`, NOT `w`. `w` is the value from the last render,
     and a blur that lands before React has rendered the last keystroke saved
     the text from one keystroke earlier — found 2026-09-24 in the browser: the
     row read "Notes", the read-out said "✓ Saved", and the note was not in the
     database. A ref holds what she actually typed, whenever the blur arrives. */
  const latest = useRef(w);
  latest.current = w;
  const edit = (next) => { touched.current = true; latest.current = next; setW(next); };
  const saveTyped = () => save(latest.current);

  const setRule = (id, text) => edit({ ...w, rules: w.rules.map((r) => (r.id === id ? { ...r, text } : r)) });
  const addRule = () => {
    const r = newRule();
    focusId.current = r.id;
    edit({ ...w, rules: [...w.rules, r] });
  };
  const removeRule = (id) => save({ ...w, rules: w.rules.filter((r) => r.id !== id) });

  const learned = w.rules.filter((r) => r.learned).length;

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: "auto", background: "var(--bg-primary)", padding: isMobile ? "16px 16px 100px" : "32px 32px 48px" }}>
      <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <PenLine size={18} color="var(--accent)" />
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "var(--text-primary)" }}>Voice</h2>
            {/* THE READ-OUT. Amber and named when it fails (rule 1). */}
            <p style={{ margin: "2px 0 0", fontSize: 12.5, color: status === "failed" ? "var(--warn-text)" : "var(--text-secondary)" }}>
              {status === "failed" ? "That didn't save. Check your connection and try again."
                : status === "saving" ? "Saving…"
                : status === "saved" ? "✓ Saved"
                : "How you write, everywhere and tool by tool. Brill follows it in everything it drafts for you."}
            </p>
          </div>
        </div>

        {/* EVERYWHERE, then the tools that differ. Her idea, 2026-09-23: one
            screen, her rules at the top and a row per tool, rather than a cog
            on eleven panels. */}
        <h3 style={section}>Everywhere</h3>

        <div style={card}>
          <h3 style={heading}>How you ask</h3>
          <p style={sub}>How hard your copy asks people to buy or book.</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {WRITING_ASK.map((a) => (
              <Choice key={a.id} on={(w.ask || DEFAULT_ASK) === a.id} title={a.hint}
                onClick={() => save({ ...w, ask: a.id })}>{a.label}</Choice>
            ))}
          </div>
          <p style={{ ...sub, margin: "10px 0 0" }}>{WRITING_ASK.find((a) => a.id === (w.ask || DEFAULT_ASK)).hint}</p>
        </div>

        <div style={card}>
          <h3 style={heading}>Urgency</h3>
          <p style={sub}>Are you comfortable naming a deadline or the cost of waiting? Brill never invents one either way.</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {WRITING_URGENCY.map((u) => (
              <Choice key={u.id} on={w.urgency === u.id} title={u.hint}
                onClick={() => save({ ...w, urgency: w.urgency === u.id ? null : u.id })}>{u.label}</Choice>
            ))}
          </div>
        </div>

        <div style={card}>
          <h3 style={heading}>Habits</h3>
          <p style={sub}>Left unset, each tool uses what suits it: a website allows one dash per section, a text message none.</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {WRITING_HABITS.map((h) => (
              <div key={h.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ flex: "1 1 180px", fontSize: 14, color: "var(--text-primary)" }}>{h.label}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <Choice on={w.habits[h.id] === "use"} onClick={() => save({ ...w, habits: toggleHabit(w.habits, h.id, "use") })}>Use</Choice>
                  <Choice on={w.habits[h.id] === "avoid"} onClick={() => save({ ...w, habits: toggleHabit(w.habits, h.id, "avoid") })}>Avoid</Choice>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={card}>
          <h3 style={heading}>Words you own</h3>
          <p style={sub}>Words from your niche or your method that Brill must keep, even ones it would normally cut as a cliché, like "pivot" for a career-change coach.</p>
          <textarea value={w.words} onChange={(e) => edit({ ...w, words: e.target.value })} onBlur={saveTyped}
            placeholder="e.g. pivot, Secret Sauce, the Aligned Business Method" rows={2}
            style={{ ...field, resize: "vertical", lineHeight: 1.5 }} />
        </div>

        <div style={card}>
          <h3 style={heading}>Your rules</h3>
          <p style={sub}>
            Anything else about how you write, one rule per line.
            {learned > 0 && ` ${learned} of these were learned from your edits. Change or remove them like your own.`}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {w.rules.map((r) => (
              <div key={r.id} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <textarea value={r.text} rows={1}
                    ref={(el) => { if (el && focusId.current === r.id) { el.focus(); focusId.current = null; } }}
                    onChange={(e) => setRule(r.id, e.target.value)} onBlur={saveTyped}
                    placeholder="e.g. End on a quiet insight, not a hard sell."
                    style={{ ...field, resize: "vertical", lineHeight: 1.5, minHeight: 40 }} />
                  {r.learned && <span style={{ fontSize: 11.5, color: "var(--text-secondary)" }}>Learned from your edits</span>}
                </div>
                <button onClick={() => removeRule(r.id)} aria-label="Remove this rule" title="Remove this rule"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)", padding: 10, display: "flex" }}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
          <button onClick={addRule}
            style={{ marginTop: w.rules.length ? 12 : 0, display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600,
              border: "1.5px solid var(--accent)", background: "transparent", color: "var(--accent)" }}>
            <Plus size={14} /> Add a rule
          </button>
        </div>

        <h3 style={section}>Tool by tool</h3>
        <p style={{ ...sub, margin: 0 }}>Where one tool should sound different, say so here. Everything else follows your settings above.</p>
        <ToolRows w={w} save={save} edit={edit} saveTyped={saveTyped} />

        <p style={{ ...sub, margin: 0 }}>
          These win over Brill's general writing rules and your Voice Profile. They never let copy claim something untrue: no invented results, testimonials or deadlines, whatever is written here.
        </p>
      </div>
    </div>
  );
}

/* THE ROWS, one per tool. Collapsed, a row says only whether that tool has
   its own settings ("Direct response · Notes") or follows hers ("Your
   default") — scanning eleven rows is the job. The whole strip opens it (rule
   2). Inside: how Brill writes there, read from the rules it is actually given
   (api/chat.js, mode "voices" — no AI call), then her two settings for it. */
function useVoiceReadout() {
  const { session } = useSession();
  const [voices, setVoices] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const token = await session?.getToken();
        const res = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ mode: "voices" }),
        });
        const data = await res.json();
        if (!gone) { if (res.ok && data.voices) setVoices(data.voices); else setFailed(true); }
      } catch {
        if (!gone) setFailed(true);
      }
    })();
    return () => { gone = true; };
  }, [session]);
  return { voices, failed };
}

function ToolRows({ w, save, edit, saveTyped }) {
  const [open, setOpen] = useState(null);
  const { voices, failed } = useVoiceReadout();
  const mine = WRITING_ASK.find((a) => a.id === (w.ask || DEFAULT_ASK));
  return (
    <div style={{ ...card, padding: 0, overflow: "hidden" }}>
      {VOICE_TOOLS.map((tool, i) => {
        const isOpen = open === tool.id;
        const t = w.tools?.[tool.id] || {};
        const voice = voices?.[tool.id];
        const summary = toolSummary(w, tool.id);
        return (
          <div key={tool.id} style={{ borderTop: i ? "1px solid var(--border-subtle)" : "none" }}>
            <button onClick={() => setOpen(isOpen ? null : tool.id)} aria-expanded={isOpen}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "13px 18px", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
              {isOpen ? <ChevronDown size={14} color="var(--text-secondary)" /> : <ChevronRight size={14} color="var(--text-secondary)" />}
              <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{tool.label}</span>
              <span style={{ fontSize: 12.5, color: summary === "Your default" ? "var(--text-secondary)" : "var(--accent)" }}>{summary}</span>
            </button>
            {isOpen && (
              <div style={{ padding: "0 18px 18px 42px", display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <div style={{ ...heading, fontSize: 11.5, marginBottom: 6 }}>How Brill writes here</div>
                  {voice ? (
                    <>
                      <p style={{ ...sub, margin: "0 0 6px" }}>{voice.reads}</p>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.6 }}>
                        {voice.lines.map((l) => <li key={l}>{l}</li>)}
                      </ul>
                    </>
                  ) : (
                    <p style={{ ...sub, margin: 0 }}>{failed ? "Couldn't load this just now. Your settings below still work." : "Loading…"}</p>
                  )}
                </div>
                {LEARNING_TOOLS.includes(tool.id) && <LearnedFromEdits tool={tool} t={t} w={w} save={save} edit={edit} saveTyped={saveTyped} />}
                <div>
                  <div style={{ ...heading, fontSize: 11.5, marginBottom: 6 }}>How hard it asks here</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Choice on={!t.ask} onClick={() => save(setTool(w, tool.id, { ask: null }))}>Your default ({mine.label})</Choice>
                    {WRITING_ASK.map((a) => (
                      <Choice key={a.id} on={t.ask === a.id} title={a.hint} onClick={() => save(setTool(w, tool.id, { ask: a.id }))}>{a.label}</Choice>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={{ ...heading, fontSize: 11.5, marginBottom: 6 }}>Your notes for {tool.label}</div>
                  <textarea value={t.notes || ""} rows={2}
                    onChange={(e) => edit(setTool(w, tool.id, { notes: e.target.value }))} onBlur={saveTyped}
                    placeholder={`e.g. ${NOTE_EXAMPLES[tool.id] || "What should be different here?"}`}
                    style={{ ...field, resize: "vertical", lineHeight: 1.5 }} />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* WHAT BRILL HAS LEARNED FROM HER EDITS IN THIS TOOL — "what your Social
   voice is" (her words, 2026-09-30). Each rule is hers to change or remove like
   her own (her call, 2026-09-23). The button is live only when there is
   something new: the count comes from a free status read when the row opens,
   so a press on nothing new never costs a message. */
function LearnedFromEdits({ tool, t, w, save, edit, saveTyped }) {
  const { session } = useSession();
  const [status, setStatus] = useState(null);   // { fresh, min, lastLearnedAt, lastLearnedFrom } | { error }
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState("");
  const call = async (body) => {
    const token = await session?.getToken();
    const res = await fetch("/api/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ mode: "learn_voice", moduleId: tool.id, tool: tool.id, ...body }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(res.status === 429 ? "That's the usage limit for now — try again later." : data.error || `Error ${res.status}`);
    return data;
  };
  useEffect(() => {
    if (!session) return undefined;
    let gone = false;
    (async () => {
      try {
        const token = await session.getToken();
        const res = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ mode: "learn_voice", moduleId: tool.id, tool: tool.id, action: "status" }),
        });
        const data = await res.json().catch(() => ({}));
        if (!gone) setStatus(res.ok && data.ok ? data : { error: data.error || "Couldn't check your edits just now." });
      } catch {
        if (!gone) setStatus({ error: "Couldn't check your edits just now." });
      }
    })();
    return () => { gone = true; };
  }, [session, tool.id]);

  const learnNow = async () => {
    if (busy) return;
    setBusy(true);
    setSaid("");
    try {
      const r = await call({ action: "learn" });
      if (!r.ok) { setSaid(r.error || "Nothing was learned."); return; }
      if (r.writing) await save(readWriting(r.writing));
      setSaid(r.note || (r.added?.length
        ? `Learned ${r.added.length} new thing${r.added.length === 1 ? "" : "s"} from ${r.from} edit${r.from === 1 ? "" : "s"}. They're below, and Brill follows them from now on.`
        : `Read ${r.from} edits — nothing new stood out yet. Those edits won't be read again.`));
      setStatus((s) => ({ ...(s || {}), fresh: r.writing ? 0 : (s?.fresh || 0), lastLearnedAt: r.writing ? new Date().toISOString() : s?.lastLearnedAt, lastLearnedFrom: r.from || s?.lastLearnedFrom }));
    } catch (err) {
      setSaid(err.message || "Couldn't reach Brill just then. Nothing was changed.");
    } finally {
      setBusy(false);
    }
  };

  const learned = t.learned || [];
  const setLearned = (next) => setTool(w, tool.id, { learned: next });
  const fresh = status?.fresh || 0;
  const min = status?.min || 2;
  const can = !!status && !status.error && fresh >= min && !busy;
  const line = !status ? "Checking your edits…"
    : status.error ? status.error
    : fresh >= min ? `${fresh} new edit${fresh === 1 ? "" : "s"} to learn from.`
    : fresh === 1 ? "1 new edit so far. Brill needs at least 2 to spot a pattern."
    : "Up to date — edit a few more drafts and there'll be something new.";
  const last = status?.lastLearnedAt ? ` Last learned from ${status.lastLearnedFrom} edit${status.lastLearnedFrom === 1 ? "" : "s"} on ${new Date(status.lastLearnedAt).toLocaleDateString(undefined, { day: "numeric", month: "short" })}.` : "";

  return (
    <div>
      <div style={{ ...heading, fontSize: 11.5, marginBottom: 6 }}>Learned from your edits</div>
      <p style={{ ...sub, margin: "0 0 10px" }}>
        Every {tool.label} post keeps Brill's words next to yours. Brill reads what you change and turns it into rules for itself here.
      </p>
      {learned.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
          {learned.map((r) => (
            <div key={r.id} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <textarea value={r.text} rows={1} aria-label="A rule learned from your edits"
                onChange={(e) => edit(setLearned(learned.map((x) => (x.id === r.id ? { ...x, text: e.target.value } : x))))} onBlur={saveTyped}
                style={{ ...field, resize: "vertical", lineHeight: 1.5, minHeight: 40, fontSize: 13.5 }} />
              <button onClick={() => save(setLearned(learned.filter((x) => x.id !== r.id)))} aria-label="Remove this learned rule" title="Remove it — Brill stops following it"
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)", padding: 10, display: "flex" }}>
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button onClick={learnNow} disabled={!can} title="Brill reads your new edits and adds what it finds. About $0.02."
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, fontFamily: "inherit", fontSize: 13, fontWeight: 600,
            border: "1.5px solid var(--accent)", background: "transparent", color: "var(--accent)", cursor: can ? "pointer" : "default", opacity: can ? 1 : 0.5 }}>
          <Sparkles size={14} /> {busy ? "Brill is reading your edits…" : "Learn from my edits"}
        </button>
        <span style={{ fontSize: 12.5, color: status?.error ? "var(--warn-text)" : "var(--text-secondary)", lineHeight: 1.5 }}>{line}{last}</span>
      </div>
      {said && <p style={{ ...sub, margin: "10px 0 0", color: "var(--text-primary)" }}>{said}</p>}
    </div>
  );
}

/* A placeholder per tool, so the box suggests what kind of note belongs. */
const NOTE_EXAMPLES = {
  website_copy: "More authoritative here than on social.",
  outreach_copy: "Read like a text from a friend, never a campaign.",
  social_generator: "Lighter and more playful than my website.",
  newsletter_copy: "Chatty, like a letter to one reader.",
  call_script_copy: "Leave room for them to talk.",
  video_script_copy: "Say it like I'm talking to one person on camera.",
  offer_ladder: "Plain and specific about what's included.",
  course_copy: "Teach it the way I'd explain it in the room.",
  presentation_copy: "Big claims on the slides, the story in the notes.",
  content_studio: "Keep the plan practical, not aspirational.",
};